import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Container, Chip, Skeleton, Stack,
    FormControl, Select, MenuItem, TextField,
    InputAdornment, IconButton,
} from '@mui/material';
import TwoWheelerIcon      from '@mui/icons-material/TwoWheeler';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import LocationOnIcon      from '@mui/icons-material/LocationOn';
import ReceiptIcon         from '@mui/icons-material/Receipt';
import PersonIcon          from '@mui/icons-material/Person';
import SearchIcon          from '@mui/icons-material/Search';
import CloseIcon           from '@mui/icons-material/Close';
import axios               from '../api/axiosConfig';
import { useAuth }         from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';

const RENTAL_STATUSES = ['PENDING', 'SCHEDULED', 'CONFIRMED', 'ACTIVE', 'COMPLETED', 'CANCELLED'];

const STATUS_CONFIG = {
    PENDING:   { color: 'default',  label: 'Pending'   },
    SCHEDULED: { color: 'info',     label: 'Scheduled' },
    CONFIRMED: { color: 'primary',  label: 'Confirmed' },
    ACTIVE:    { color: 'warning',  label: 'Active'    },
    COMPLETED: { color: 'success',  label: 'Completed' },
    CANCELLED: { color: 'error',    label: 'Cancelled' },
};

function CardSkeleton() {
    return (
        <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: '16px',
            border: '1px solid rgba(44,31,20,0.08)', mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Skeleton variant="text" width={200} height={26} />
                <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: '20px' }} />
            </Box>
            <Stack direction="row" spacing={3}>
                <Skeleton variant="text" width={140} height={18} />
                <Skeleton variant="text" width={120} height={18} />
                <Skeleton variant="text" width={100} height={18} />
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Skeleton variant="rounded" width={160} height={32} sx={{ borderRadius: '20px' }} />
            </Box>
        </Box>
    );
}

export default function RentalsPage() {
    const { user } = useAuth();

    const [rentals,      setRentals]      = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [search,       setSearch]       = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    useEffect(() => {
        axios.get('/rentals/view')
            .then(res => setRentals(res.data?.content || []))
            .catch(() => setRentals([]))
            .finally(() => setLoading(false));
    }, []);

    const handleStatusChange = (rentalId, newStatus) => {
        axios.put(`/rentals/edit/${rentalId}/status?status=${newStatus}`)
            .then(res => setRentals(prev => prev.map(r => r.id === rentalId ? res.data : r)))
            .catch(err => console.error('Status update failed', err));
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rentals.filter(r => {
            const matchSearch = !q ||
                (r.offerName || '').toLowerCase().includes(q) ||
                (r.districtAddress || '').toLowerCase().includes(q) ||
                (r.customerId || '').toLowerCase().includes(q);
            const matchStatus = statusFilter === 'ALL' || r.status === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [rentals, search, statusFilter]);

    const counts = useMemo(() => {
        const c = { ALL: rentals.length };
        RENTAL_STATUSES.forEach(s => { c[s] = rentals.filter(r => r.status === s).length; });
        return c;
    }, [rentals]);

    const formatDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    };

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* ── Header ───────────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: WALNUT, py: 5, px: { xs: 2, md: 6 } }}>
                <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.2em', fontWeight: 700 }}>
                    Staff Management
                </Typography>
                <Typography variant="h4" fontWeight={800}
                            sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif", mb: 0.5 }}>
                    Rentals Management
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
                    {[
                        { label: 'Total',     value: counts.ALL,       color: 'rgba(255,255,255,0.85)' },
                        { label: 'Pending',   value: counts.PENDING,   color: 'rgba(255,255,255,0.6)' },
                        { label: 'Scheduled', value: counts.SCHEDULED, color: '#64b5f6' },
                        { label: 'Active',    value: counts.ACTIVE,    color: GOLD },
                        { label: 'Completed', value: counts.COMPLETED, color: '#81c784' },
                        { label: 'Cancelled', value: counts.CANCELLED, color: '#e57373' },
                    ].map(({ label, value, color }) => (
                        <Box key={label} sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>
                                {loading ? '—' : value}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem' }}>
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>

            <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 5 } }}>
                {/* ── Filters ─────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Search by offer, district or customer…"
                               value={search} onChange={e => setSearch(e.target.value)}
                               InputProps={{
                                   startAdornment: (
                                       <InputAdornment position="start">
                                           <SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                                       </InputAdornment>
                                   ),
                                   endAdornment: search ? (
                                       <InputAdornment position="end">
                                           <IconButton size="small" onClick={() => setSearch('')}>
                                               <CloseIcon sx={{ fontSize: '0.9rem' }} />
                                           </IconButton>
                                       </InputAdornment>
                                   ) : null,
                               }}
                               sx={{ width: 300, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} />
                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                        {['ALL', ...RENTAL_STATUSES].map(s => (
                            <Chip key={s}
                                  label={s === 'ALL' ? `All (${counts.ALL})` : `${STATUS_CONFIG[s]?.label} (${counts[s]})`}
                                  onClick={() => setStatusFilter(s)}
                                  color={statusFilter === s ? (s === 'ALL' ? 'default' : STATUS_CONFIG[s]?.color) : 'default'}
                                  variant={statusFilter === s ? 'filled' : 'outlined'}
                                  sx={{ fontWeight: 700, cursor: 'pointer',
                                      ...(statusFilter === s && s === 'ALL' && {
                                          bgcolor: WALNUT, color: 'white'
                                      })
                                  }} />
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary', fontWeight: 600 }}>
                        {loading ? 'Loading…' : `${filtered.length} rental${filtered.length !== 1 ? 's' : ''}`}
                    </Typography>
                </Box>

                {/* ── Rental list ───────────────────────────────────────────── */}
                {loading
                    ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
                    : filtered.length === 0
                        ? (
                            <Box sx={{ py: 10, textAlign: 'center', bgcolor: 'white',
                                borderRadius: '20px', border: '2px dashed rgba(44,31,20,0.12)' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mb: 1 }}>
                                    No rentals found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {statusFilter !== 'ALL' ? 'Try a different status filter.' : 'No rentals have been created yet.'}
                                </Typography>
                            </Box>
                        )
                        : filtered.map(rental => {
                            const statusCfg = STATUS_CONFIG[rental.status] || { color: 'default', label: rental.status };
                            return (
                                <Box key={rental.id} sx={{ bgcolor: 'white', borderRadius: '16px', mb: 2,
                                    border: '1px solid rgba(44,31,20,0.08)',
                                    boxShadow: '0 2px 8px rgba(44,31,20,0.05)',
                                    transition: 'box-shadow 0.2s',
                                    '&:hover': { boxShadow: '0 6px 20px rgba(44,31,20,0.1)' } }}>
                                    <Box sx={{ p: 2.5 }}>
                                        {/* Top row */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                <Box sx={{ color: TERRACOTTA, display: 'flex' }}>
                                                    <TwoWheelerIcon sx={{ fontSize: '1.2rem' }} />
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="h6" fontWeight={800}
                                                                sx={{ fontFamily: "'Playfair Display', Georgia, serif",
                                                                    color: WALNUT, lineHeight: 1.25, fontSize: '1.05rem' }}>
                                                        {rental.offerName || 'Rental'}
                                                    </Typography>
                                                    {rental.days != null && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            {rental.days} day{rental.days !== 1 ? 's' : ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Chip label={statusCfg.label} color={statusCfg.color} size="small"
                                                  sx={{ fontWeight: 700, borderRadius: '20px' }} />
                                        </Box>

                                        {/* Meta row */}
                                        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 1.5 }}>
                                            {rental.startTime && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTimeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(rental.startTime)}
                                                        {rental.endTime && ` → ${formatDate(rental.endTime)}`}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {rental.districtAddress && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LocationOnIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {rental.districtAddress}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {rental.customerId && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PersonIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary"
                                                                sx={{ fontFamily: 'monospace' }}>
                                                        {rental.customerId.slice(0, 8)}…
                                                    </Typography>
                                                </Box>
                                            )}
                                            {rental.billId && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <ReceiptIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary"
                                                                sx={{ fontFamily: 'monospace' }}>
                                                        {rental.billId.slice(0, 8)}…
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>

                                        {/* Actions */}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5,
                                            justifyContent: 'flex-end' }}>
                                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                                <Select value={rental.status}
                                                        onChange={e => handleStatusChange(rental.id, e.target.value)}
                                                        sx={{ borderRadius: '20px', fontSize: '0.75rem',
                                                            fontWeight: 700, height: 32,
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(196,98,45,0.3)'
                                                            } }}>
                                                    {RENTAL_STATUSES.map(s => (
                                                        <MenuItem key={s} value={s} sx={{ fontSize: '0.82rem' }}>
                                                            {STATUS_CONFIG[s]?.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })
                }
            </Container>
        </Box>
    );
}
